import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star, StarHalf } from 'lucide-react';
import { userApi } from '../../config';

interface Review {
  _id?: string;
  id?: string;
  name?: string;
  customerName?: string;
  user?: { name?: string; avatar?: string };
  rating?: number | string;
  comment?: string;
  review?: string;
  text?: string;
  serviceName?: string;
  practitionerName?: string;
  location?: string;
  city?: string;
  avatar?: string;
  isVerified?: boolean;
  createdAt?: string;
}

// Preserved Community Testimonials (Ensures the homepage is NEVER empty and retains historical reviews)
const PRESERVED_COMMUNITY_TESTIMONIALS: Review[] = [
  {
    _id: "legacy-1",
    name: "Dhawal Goswami",
    rating: 5,
    comment: "I had a wonderful experience with the yoga classes. The instructor is highly experienced and knowledgeable, which truly reflects in the quality of the sessions. The class timings are well-scheduled and convenient, and the overall teaching approach is both professional and effective.",
    serviceName: "Yoga Classes",
    location: "Wellness Member",
    isVerified: true
  },
  {
    _id: "legacy-2",
    name: "Deepak Mudaliar",
    rating: 5,
    comment: "Metafit has some amazing experts in the field of Ayurvedic and naturopathy. They help you grow healthy organically and efficiently without any major expenses. Must enroll for a healthy lifestyle.",
    serviceName: "Ayurveda & Naturopathy",
    location: "Local Guide",
    isVerified: true
  },
  {
    _id: "legacy-3",
    name: "Rekha Soni",
    rating: 5,
    comment: "Absolutely love the energies of Meenakshi ji, the way she promotes herself with so well equipped knowledge of food, fitness and yoga … and the love and compassion she gives to everyone.",
    serviceName: "Fitness & Wellness",
    location: "Verified Member",
    isVerified: true
  },
  {
    _id: "legacy-4",
    name: "Rohit Paliwal",
    rating: 5,
    comment: "Metafit Wellness is truly the best platform for holistic wellness! It's amazing to find yoga, Ayurveda, and naturopathy—all in one place. The experience is seamless, and the quality of experts is top-notch.",
    serviceName: "Holistic Wellness",
    location: "Local Guide",
    isVerified: true
  },
  {
    _id: "legacy-5",
    name: "Mahima Sarupria",
    rating: 5,
    comment: "They have experienced and qualified yoga teachers to help you through your fitness journey. Best of all, the instructors are truly dedicated to your wellness goals and progress.",
    serviceName: "Yoga Teachers Training",
    location: "Health Enthusiast",
    isVerified: true
  },
  {
    _id: "legacy-6",
    name: "B. Bishnoi",
    rating: 5,
    comment: "Absolutely love the energy and intensity at MetaFit – every session pushes me to my limits and leaves me feeling stronger! The community here is supportive and motivating.",
    serviceName: "Fitness Programs",
    location: "Fitness Member",
    isVerified: true
  },
  {
    _id: "legacy-7",
    name: "Meenakshi Gupta",
    rating: 5,
    comment: "Highly recommended if you're looking for a one stop solution for leading a healthy lifestyle. Excellent wellness solution with everything you need in one platform.",
    serviceName: "Holistic Health",
    location: "Wellness Advocate",
    isVerified: true
  },
  {
    _id: "legacy-8",
    name: "Ayushi Jain",
    rating: 5,
    comment: "Very helpful for my weight loss journey. The personalized guidance and expert support have made a real difference in achieving my fitness goals.",
    serviceName: "Weight Loss Program",
    location: "Weight Loss Member",
    isVerified: true
  }
];

const renderStars = (rawRating?: number | string) => {
  const ratingNum = typeof rawRating === 'number' ? rawRating : parseFloat(String(rawRating || 0)) || 0;
  const stars = [];
  const fullStars = Math.floor(ratingNum);
  const hasHalf = ratingNum % 1 >= 0.25 && ratingNum % 1 <= 0.75;

  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push(
        <Star key={i} className="w-4 h-4 fill-[#E6A93F] text-[#E6A93F]" aria-hidden="true" />
      );
    } else if (i === fullStars && hasHalf) {
      stars.push(
        <StarHalf key={i} className="w-4 h-4 fill-[#E6A93F] text-[#E6A93F]" aria-hidden="true" />
      );
    } else if (i === fullStars && ratingNum % 1 > 0.75) {
      stars.push(
        <Star key={i} className="w-4 h-4 fill-[#E6A93F] text-[#E6A93F]" aria-hidden="true" />
      );
    } else {
      stars.push(
        <Star key={i} className="w-4 h-4 text-gray-200 fill-gray-100" aria-hidden="true" />
      );
    }
  }
  return stars;
};

const getInitials = (name?: string): string => {
  if (!name) return 'U';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AVATAR_BG_COLORS = ['#3F6652', '#B9576B', '#57628F', '#C98726', '#2C4A3B'];

const ReviewsSection: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>(PRESERVED_COMMUNITY_TESTIMONIALS);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    const fetchRealReviews = async () => {
      try {
        const response = await userApi.get<any>('/reviews');
        if (isMounted && response.data) {
          const rawList: Review[] = Array.isArray(response.data)
            ? response.data
            : response.data.reviews || response.data.data || [];

          const validDbReviews = rawList.filter(
            (r: Review) =>
              (r.comment || r.review || r.text) &&
              (r.name || r.customerName || r.user?.name)
          );

          // Deduplicate: Keep new DB reviews at the top, then append legacy testimonials that aren't already in DB
          const combined = [
            ...validDbReviews,
            ...PRESERVED_COMMUNITY_TESTIMONIALS.filter(
              (legacy) =>
                !validDbReviews.some(
                  (dbR) =>
                    (dbR.name || dbR.customerName)?.toLowerCase().trim() ===
                      legacy.name?.toLowerCase().trim() &&
                    (dbR.comment || dbR.review)?.trim() === legacy.comment?.trim()
                )
            ),
          ];

          setReviews(combined);
        }
      } catch (err) {
        // In case of network error, gracefully retain preserved community testimonials
        if (isMounted) {
          setReviews(PRESERVED_COMMUNITY_TESTIMONIALS);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchRealReviews();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <section id="testimonials" className="bg-[#FFFFFF]" style={{ paddingTop: "20px", paddingBottom: "36px" }}>
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-4 sm:mb-5"
        >
          <div className="max-w-2xl">
            <h2 className="font-bold tracking-tight text-[16px] md:text-[18px] leading-tight m-0 text-slate-900">
              What customers say after their first session.
            </h2>
          </div>
        </motion.div>

        {/* Reviews Grid (Always Populated & Responsive) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {reviews.map((r, index) => {
            const customerName = r.customerName || r.name || r.user?.name || 'Verified Customer';
            const reviewText = r.comment || r.review || r.text || '';
            let rawLoc: any = (r as any).location || (r as any).city || r.serviceName;
            let city = '';
            if (typeof rawLoc === 'string') {
              if (rawLoc.startsWith('{') && rawLoc.endsWith('}')) {
                try {
                  const parsed = JSON.parse(rawLoc);
                  city = parsed.city || parsed.state || parsed.address || '';
                } catch {
                  city = '';
                }
              } else {
                city = rawLoc;
              }
            } else if (rawLoc && typeof rawLoc === 'object') {
              city = rawLoc.city || rawLoc.state || rawLoc.address || '';
            }
            const rating = r.rating || 5;
            const avatarColor = AVATAR_BG_COLORS[index % AVATAR_BG_COLORS.length];

            return (
              <motion.div
                key={r._id || r.id || index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
                className="group bg-white border border-[#15302B]/[0.06] rounded-[20px] p-5 sm:p-6 shadow-[0_2px_12px_-4px_rgba(21,48,43,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_24px_-8px_rgba(21,48,43,0.12)] flex flex-col relative overflow-hidden"
              >
                <div className="absolute -top-6 -left-4 text-[#E6A93F]/10 font-serif text-[100px] leading-none select-none group-hover:text-[#E6A93F]/20 transition-colors duration-500">
                  "
                </div>
                <div className="relative z-10 flex-grow">
                  <div className="flex items-center gap-1 mb-2.5">
                    {renderStars(rating)}
                  </div>
                  <p className="font-inter text-[13.5px] sm:text-[14px] leading-relaxed text-[#3B5750] mb-4 font-normal">
                    "{reviewText}"
                  </p>
                </div>
                <div className="relative z-10 flex items-center gap-3 pt-3.5 border-t border-gray-100 mt-auto">
                  {r.avatar || r.user?.avatar ? (
                    <img
                      src={r.avatar || r.user?.avatar}
                      alt={customerName}
                      className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover border border-gray-200"
                      onError={(e) => {
                        (e.currentTarget as HTMLElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div
                      className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-serif text-sm sm:text-base flex-shrink-0"
                      style={{ backgroundColor: avatarColor }}
                    >
                      {getInitials(customerName)}
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-[#15302B] text-[13.5px] leading-tight">
                      {customerName}
                    </div>
                    {city && (
                      <div className="text-[11.5px] text-[#556963] font-medium leading-tight mt-0.5">
                        {city}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
